import { FC, useEffect, useMemo, useState, useTransition } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState } from "recoil";

import { FramerTooltip } from "@ryde/controls";
import { useGlobalEffectActions, usePageContext } from "@ryde/redux";
import {
  liveMapHiddenDelayStatusesAtomFamily,
  NO_VALUE_DISPLAY,
  RideDelayStatusEnum,
  TOOLTIP_DELAY_BUTTONS,
} from "@ryde/shared";

import { areHiddenDelayStatusesEqual } from "../live-map.controls.utils";
import { ILiveMapGridStatusCount } from "../use-live-map-toolbar-stats";
import {
  LiveMapGridStatusBreakdownRowStyled,
  LiveMapGridStatusBreakdownStyled,
} from "./live-map.grid-status-breakdown.styled";
import {
  LiveMapGridStatusChipButtonStyled,
  LiveMapGridStatusChipTooltipTriggerStyled,
} from "./live-map.grid-status-chip.styled";
import { LiveMapPlannedForNowToggle } from "./live-map.planned-for-now-toggle";

export interface ILiveMapGridStatusBreakdownProps {
  statusCounts: readonly ILiveMapGridStatusCount[];
  /** Compact strip layout for mobile stats row (ellipsis labels, single line). */
  isMobileStrip?: boolean;
}

export const LiveMapGridStatusBreakdown: FC<
  ILiveMapGridStatusBreakdownProps
> = ({ statusCounts, isMobileStrip = false }) => {
  const { entity } = usePageContext();
  const { t } = useTranslation();
  const { resetPaginationAppEvent } = useGlobalEffectActions();
  const [hiddenStatuses, setHiddenStatuses] = useRecoilState(
    liveMapHiddenDelayStatusesAtomFamily(entity),
  );
  const [optimisticHiddenStatuses, setOptimisticHiddenStatuses] = useState<
    readonly RideDelayStatusEnum[] | null
  >(null);
  const [, startTransition] = useTransition();

  const effectiveHiddenStatuses = optimisticHiddenStatuses ?? hiddenStatuses;

  useEffect(() => {
    if (
      optimisticHiddenStatuses !== null &&
      areHiddenDelayStatusesEqual(optimisticHiddenStatuses, hiddenStatuses)
    ) {
      setOptimisticHiddenStatuses(null);
    }
  }, [hiddenStatuses, optimisticHiddenStatuses]);

  const hiddenStatusSet = useMemo(
    () => new Set(effectiveHiddenStatuses),
    [effectiveHiddenStatuses],
  );

  const toggleStatus = useEvent((status: RideDelayStatusEnum) => {
    const current = optimisticHiddenStatuses ?? hiddenStatuses;
    const next = current.includes(status)
      ? current.filter(item => item !== status)
      : [...current, status];

    setOptimisticHiddenStatuses(next);

    startTransition(() => {
      setHiddenStatuses(next);
      resetPaginationAppEvent();
    });
  });

  return (
    <LiveMapGridStatusBreakdownRowStyled
      className={
        isMobileStrip
          ? "live-map-grid-status-breakdown-row--mobile-strip"
          : undefined
      }
    >
      {!statusCounts.length && !isMobileStrip ? (
        <span>{NO_VALUE_DISPLAY}</span>
      ) : (
        <LiveMapGridStatusBreakdownStyled
          className={
            isMobileStrip
              ? "live-map-grid-status-breakdown--mobile-strip"
              : undefined
          }
        >
          {statusCounts.map(({ status, count }) => {
            const isVisible = !hiddenStatusSet.has(status);
            const label = t(`rideMap.delayStatus.${status}`);
            const tooltip = isVisible
              ? t("rideMap.HIDE_DELAY_STATUS_GROUP", { status: label })
              : t("rideMap.SHOW_DELAY_STATUS_GROUP", { status: label });

            return (
              <FramerTooltip
                key={status}
                content={tooltip}
                mouseEnterDelay={TOOLTIP_DELAY_BUTTONS}
              >
                <LiveMapGridStatusChipTooltipTriggerStyled
                  className={
                    isMobileStrip
                      ? "live-map-grid-status-chip-tooltip-trigger"
                      : undefined
                  }
                >
                  <LiveMapGridStatusChipButtonStyled
                    $status={status}
                    $isVisible={isVisible}
                    aria-pressed={isVisible ? "true" : "false"}
                    aria-label={tooltip}
                    onClick={() => toggleStatus(status)}
                  >
                    <span className="live-map-grid-status-chip-label">
                      {label}
                    </span>
                    <span className="live-map-grid-status-chip-count">
                      {count}
                    </span>
                  </LiveMapGridStatusChipButtonStyled>
                </LiveMapGridStatusChipTooltipTriggerStyled>
              </FramerTooltip>
            );
          })}
        </LiveMapGridStatusBreakdownStyled>
      )}
      <LiveMapPlannedForNowToggle isMobileStrip={isMobileStrip} />
    </LiveMapGridStatusBreakdownRowStyled>
  );
};
